import Tippy from '@tippyjs/react';

import { differenceInDays } from 'date-fns';

import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdModeComment } from 'react-icons/md';
import { registerInteraction } from 'services/interactions.service';
import { useModal } from 'context/ModalContext';
import { getKPISentHistoryPag, getServerTime } from 'services/dashboard.service';
import { FiEdit3 } from 'react-icons/fi';
import { getActionById, updateRewardBehavior } from 'services/actionlog.service';
import { TbSearch } from 'react-icons/tb';
import { convertEmojis, itemsPerPageExtrato } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import { BsArrowReturnLeft } from 'react-icons/bs';
import { TbMessageCancel } from 'react-icons/tb';
import ModalCancelRecognition from 'components/modal-cancel-recognition';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import * as ui from 'components/reconhecimento-ui';
import BuscaDePessoas from 'components/ui/busca-de-pessoas';
import { useRequisicaoMaisRecente } from 'hooks/useRequisicaoMaisRecente';
import { TITULO_DE_SECAO } from 'styles';

const CoinLabel: any = {
  uau: '#UAU',
  mandou: '#MANDOUBEM',
  valeu: '#VALEU',
};

const BehaviorLabel: any = {
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  foco: 'Foco no Cliente',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Integradora',
};

interface Props {
  activeModal: (data: { message: string; id: string }) => void;
  filter?: any;
  message?: { message: string; id: string } | null;
}

export default function Recognition(data: Props) {
  const { activeModal, message, filter } = data;
  const { user, currentCycle } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const [time, setTime] = useState<number>();
  const [total, setTotal] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [cancelRecognitionId, setCancelRecognitionId] = useState<string | undefined>();

  const { showModal } = useModal();

  // Ref para controlar se o carregamento inicial já foi feito
  const initialLoadDone = useRef(false);
  // Trocar a chave remonta o campo de busca vazio, depois de um cancelamento.
  const [versaoDaBusca, setVersaoDaBusca] = useState(0);
  const requisicoes = useRequisicaoMaisRecente();

  const [loadingList, setLoadingList] = useState(true);

  function hasLike(item: any) {
    if (item?.attributes?.status !== 'OK') {
      return false;
    }

    return !!item?.interaction.find((i: any) => i.player === user?._id && !!i.like);
  }

  function canEdit(item: any) {
    if (item?.attributes?.status !== 'OK') {
      return false;
    }

    if (item && time) {
      const differenceTime = differenceInDays(new Date(time), new Date(item.time));
      return differenceTime <= 10;
    }

    return false;
  }

  function getComment(item: any) {
    if (item?.attributes?.status !== 'OK') {
      return null;
    }

    const interaction = item.interaction?.find((e: any) => !!e.message?.trim().length);
    return interaction || null;
  }

  function getReturnComment(item: any) {
    if (
      (item?.attributes?.status !== 'devolvido' && item?.attributes?.status !== 'cancelado') ||
      !item?.returnRecognition?.message
    ) {
      return null;
    }

    return item?.returnRecognition?.message;
  }

  async function updateRecognition(data: { message: string; id: string }) {
    const resp = await getActionById(data.id);
    resp.attributes.message = data.message;

    await updateRewardBehavior(resp);

    const newList = [...list];
    const index = newList.findIndex(e => e._id === data.id);
    newList[index].attributes.message = data.message;

    setList(newList);
  }

  // Carrega o horário do servidor
  useEffect(() => {
    async function loadTime() {
      const res = await getServerTime();
      setTime(res);
    }

    if (user && currentCycle) {
      loadTime();
    }
  }, [currentCycle, user]);

  // Atualiza reconhecimento quando a prop message muda
  useEffect(() => {
    if (message?.message && message?.id) {
      updateRecognition(message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  // Função para carregar dados - recebe parâmetros explícitos para evitar problemas de timing
  const fetchData = useCallback(
    async (searchTerm: string, loadMore: boolean, currentList: any[]) => {
      const ehAUltima = requisicoes.iniciar();
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoadingList(true);
      }

      try {
        // Monta o filtro com base nos parâmetros
        const filterPayload: any = {
          ...filter,
          cycleId: filter?.cycleId || currentCycle?._id,
        };

        if (searchTerm.trim()) {
          filterPayload.search = searchTerm.trim();
        }

        // Se for "carregar mais", busca o restante (10000 para pegar tudo)
        // Se for primeira carga, busca apenas itemsPerPageExtrato
        const rangeStart = loadMore ? currentList.length : 0;
        const rangeTotal = loadMore ? 10000 : itemsPerPageExtrato;

        const response = await getKPISentHistoryPag(
          user?._id,
          filterPayload,
          rangeStart,
          rangeTotal,
          currentCycle?.date_start,
          currentCycle?.date_finish,
        );
        if (!ehAUltima()) return;

        if (loadMore) {
          // Concatena com a lista existente
          setList([...currentList, ...response.result]);
          setAllLoaded(true); // Após carregar mais, já carregou tudo
        } else {
          // Primeira carga ou nova busca: substitui a lista
          setList(response.result);
          // Se o total for maior que o que carregamos, há mais itens
          setAllLoaded(response.range.total <= itemsPerPageExtrato);
        }

        setTotal(response.range.total);
      } catch (error) {
        console.error('Erro ao carregar reconhecimentos enviados:', error);
      } finally {
        if (ehAUltima()) {
          setLoadingList(false);
          setLoadingMore(false);
        }
      }
    },
    [currentCycle?._id, currentCycle?.date_finish, currentCycle?.date_start, filter, user?._id, requisicoes],
  );

  // Carregamento inicial - só executa uma vez quando as dependências estão prontas
  useEffect(() => {
    if (user && currentCycle && filter?.cycleId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchData('', false, []);
    }
  }, [user, currentCycle, filter?.cycleId, fetchData]);

  // Busca pelo texto do campo, já aparado (AJ2-46); termo vazio volta à lista inteira (AJ2-47).
  const buscar = (termo: string) => {
    setSearchValue(termo);
    setAllLoaded(false);
    fetchData(termo, false, []);
  };

  // Handler para carregar mais
  const handleLoadMore = () => {
    if (loadingMore || allLoaded) return;
    fetchData(searchValue, true, list);
  };

  // Handler para o callback do modal de cancelamento
  const handleCancelCallback = (action: string) => {
    if (action === 'remove') {
      // Reseta e recarrega
      setAllLoaded(false);
      setSearchValue('');
      setVersaoDaBusca(versao => versao + 1);
      fetchData('', false, []);
    } else if (action === 'cantCancel') {
      setList(prevstate =>
        prevstate.map(x => {
          const obj = { ...x };
          if (obj._id === cancelRecognitionId) {
            obj.canCancelRecognition = false;
          }
          return obj;
        }),
      );
    } else if (action === 'close') {
      setCancelRecognitionId(undefined);
    }
  };

  // Determina se deve mostrar o botão "Carregar mais"
  const showLoadMoreButton = !allLoaded && total > itemsPerPageExtrato && list.length < total;

  return (
    <>
      <div>
        <div className="mb-[22px] flex flex-wrap items-center justify-between gap-4">
          <h3 className={TITULO_DE_SECAO}>
            Reconhecimentos enviados
            {total ? <span className={ui.CONTADOR_DA_LISTA}> ({total})</span> : null}
          </h3>
          <BuscaDePessoas
            key={versaoDaBusca}
            rotulo="Pesquise com nome, matrícula, prefixo ou grupo"
            placeholder="Pesquise com nome, matrícula, prefixo ou grupo"
            onBuscar={buscar}
            classeDoFormulario={ui.FORM_DE_BUSCA}
            classeDoCampo={ui.CAMPO_DE_BUSCA}
            antesDoCampo={<TbSearch size={18} className="shrink-0 text-brand-blue" aria-hidden="true" />}
            depoisDoCampo={
              <button type="submit" className="sr-only">
                Pesquisar
              </button>
            }
          />
        </div>

        {loadingList && <CartoesCarregando />}

        {!loadingList && (
          <>
            <div className={ui.GRADE_DE_CARTOES}>
              {!!list?.length &&
                list?.map((item: any) => (
                  <article
                    className={`${ui.CARTAO} ${item?.attributes?.status !== 'OK' ? 'opacity-70' : ''}`}
                    key={item?._id}
                  >
                    <div className="flex items-start gap-[18px]">
                      <ui.AvatarDoRemetente
                        dados={item.target}
                        nome={item.target?.nome_guerra || item.target?.name}
                        matricula={item.attributes?.target}
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                        <div className="flex flex-col gap-[3px]">
                          <Humanograma
                            data={{
                              ...item,
                              nome: item?.target?.name,
                              funcao: item?.target?.funcao,
                              _id: item?.attributes?.target,
                              prefixo: item?.target?.prefixo,
                              nome_dependencia: item?.target?.nome_dependencia,
                            }}
                          >
                            <p className={`${ui.LINHA_DO_NOME} cursor-pointer hover:underline`}>
                              <span className="text-sm text-content">Você enviou para </span>
                              <span className={ui.NOME}>{item.target.nome_guerra || item.target.name.split(' ')[0]}</span>{' '}
                              <span className={ui.MATRICULA}>({item.attributes.target})</span>
                            </p>
                          </Humanograma>
                          <p className={`m-0 ${ui.DATA_DE_ENVIO}`}>Enviou: {ui.formatarEnvio(item.time)}</p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <span className={ui.CHIP_MOEDA}>{CoinLabel[item.attributes.coin]}</span>
                          <span className={ui.CHIP}>{BehaviorLabel[item.attributes.behavior]}</span>

                          {!!item.total && item?.attributes?.status === 'OK' ? (
                            <span className={ui.CHIP_TOTAL}>Total = {item.total} pontos</span>
                          ) : null}

                          {item?.attributes?.status === 'devolvido' ? (
                            <span className={ui.CHIP_ALERTA}>
                              <BsArrowReturnLeft size={16} aria-hidden="true" />
                              Reconhecimento devolvido
                            </span>
                          ) : null}

                          {item?.attributes?.status === 'cancelado' ? (
                            <span className={ui.CHIP_ALERTA}>
                              <TbMessageCancel size={16} aria-hidden="true" />
                              Reconhecimento cancelado
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {!!item.attributes.message && (
                      <div className={ui.DIVISOR}>
                        <div className="flex items-center justify-between">
                          <p className={`m-0 ${ui.ROTULO_DA_MENSAGEM}`}>Reconhecimento enviado:</p>
                          {canEdit(item) && (
                            <button
                              type="button"
                              onClick={() => {
                                activeModal({
                                  id: item._id,
                                  message: item.attributes.message,
                                });
                              }}
                              className={`${ui.ACAO} cursor-pointer text-brand-blue`}
                            >
                              <FiEdit3 size={18} aria-hidden="true" /> Editar
                            </button>
                          )}
                        </div>
                        <div
                          className={`${ui.TEXTO_DA_MENSAGEM} break-words`}
                          dangerouslySetInnerHTML={{
                            __html: convertEmojis(item.attributes.message),
                          }}
                        />

                        {!!getComment(item) && (
                          <div className="mt-3">
                            <p className={`m-0 ${ui.ROTULO_DA_MENSAGEM}`}>Comentário:</p>
                            <div
                              className={ui.TEXTO_DA_MENSAGEM}
                              dangerouslySetInnerHTML={{
                                __html: convertEmojis(getComment(item).message),
                              }}
                            ></div>
                          </div>
                        )}

                        {!!getReturnComment(item) && (
                          <div className="mt-3">
                            <p className={`m-0 ${ui.ROTULO_DA_MENSAGEM}`}>
                              Motivo {item?.attributes?.status === 'cancelado' ? 'do cancelamento' : 'da devolução'}:
                            </p>
                            <div
                              className={ui.TEXTO_DA_MENSAGEM}
                              dangerouslySetInnerHTML={{
                                __html: convertEmojis(getReturnComment(item)),
                              }}
                            ></div>
                          </div>
                        )}
                      </div>
                    )}

                    {(!!hasLike(item) || !!item.canCancelRecognition) && (
                      <div className={ui.ACOES}>
                        {!!item.canCancelRecognition && (
                          <button type="button" className={ui.ACAO_ALERTA} onClick={() => setCancelRecognitionId(item._id)}>
                            <TbMessageCancel size={20} aria-hidden="true" />
                            Cancelar
                          </button>
                        )}

                        {!!hasLike(item) && (
                          <span className={ui.ACAO}>
                            <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={'fill-brand-pink-light'}>
                          <path d="M12 21s-8-4.9-8-10.4A4.6 4.6 0 0 1 12 7a4.6 4.6 0 0 1 8 3.6C20 16.1 12 21 12 21Z" />
                        </svg>
                            Curtido!
                          </span>
                        )}
                      </div>
                    )}

                    {!!item.systemMessage && (
                      <div className={`${ui.DIVISOR} text-sm italic text-content-soft`}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: item.systemMessage,
                          }}
                        ></div>
                      </div>
                    )}
                  </article>
                ))}

              {!list?.length && <p className={ui.VAZIO}>Nenhum reconhecimento enviado até o momento.</p>}
            </div>

            {showLoadMoreButton && (
              <div className="mt-8 flex w-full justify-center">
                <button disabled={loadingMore} className={ui.CARREGAR_MAIS} type="button" onClick={handleLoadMore}>
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {!!cancelRecognitionId && (
        <ModalCancelRecognition
          callback={handleCancelCallback}
          id={cancelRecognitionId}
          type="cancel"
        />
      )}
    </>
  );
}
